// The ⋮ menu on a staff Account Detail location row (legacy `pages/shared/accounts/_partials/AccountLocationMenu.vue`,
// field note backoffice.accounts.detail): 240 wide, 48 px rows split by rules, each with legacy's 21 px table icon in the
// tertiary gray before its secondary-gray label. Edit, Log into location and Add User are built; legacy's master and
// linked-location items (Import/copy location data, Link to / Unlink from Master Location, Set as Master Location) wait
// on their own plan. Remove Location Data (plan 2026-10-13-remove-location-data) ends the menu in red (Tailwind red-600 under Quasar's red warning icon), only for a
// location with live items (legacy `!requires_data`).
import { Fragment, type ReactNode } from 'react';
import { DropdownMenu } from 'radix-ui';
import { RowMenu } from '../users/UsersPage.tsx';

const ICON = 'size-[21px] shrink-0 text-text-3';

/** Legacy `components/icons/table-icons/EditIcon.vue`. */
const EditIcon = () => (
  <svg viewBox="0 0 20 20" className={ICON} aria-hidden data-icon="edit">
    <path fill="currentColor" d="m3,18c-.27,0-.52-.1-.71-.29-.25-.25-.35-.61-.26-.95l1-4c.04-.18.14-.34.26-.46L12.67,2.91c1.22-1.22,3.2-1.22,4.41,0,1.22,1.22,1.22,3.2,0,4.41l-9.38,9.38c-.13.13-.29.22-.46.26l-4,1c-.08.02-.16.03-.24.03ZM12,6.41h0l-6.59,6.59,1.59,1.58,6.59-6.58-1.59-1.59Zm2.88-2.41c-.3,0-.58.12-.79.33l-.67.67,1.59,1.58.67-.67c.44-.44.44-1.15,0-1.58-.21-.21-.5-.33-.79-.33Z" />
  </svg>
);

/** Legacy `components/icons/table-icons/LoginIcon.vue`. */
const LoginIcon = () => (
  <svg viewBox="0 0 20 20" className={ICON} aria-hidden data-icon="login">
    <path fill="currentColor" d="m11.71,9.3l-4-4c-.39-.39-1.02-.39-1.41,0s-.39,1.02,0,1.41l2.29,2.29H1c-.55,0-1,.45-1,1s.45,1,1,1h7.59l-2.29,2.29c-.39.39-.39,1.02,0,1.41.2.2.45.29.71.29s.51-.1.71-.29l4-4c.39-.39.39-1.02,0-1.41Z" />
    <path fill="currentColor" d="m10,0C15.51,0,20,4.49,20,10s-4.49,10-10,10c-1.56,0-3.06-.35-4.45-1.04-.49-.25-.7-.85-.45-1.34.25-.49.84-.7,1.34-.45,1.11.55,2.31.83,3.56.83,4.41,0,8-3.59,8-8S14.41,2,10,2c-1.25,0-2.45.28-3.56.83-.5.25-1.09.04-1.34-.45-.25-.49-.04-1.09.45-1.34C6.94.35,8.44,0,10,0Z" />
  </svg>
);

/** Legacy `components/icons/table-icons/InviteUserIcon.vue`. */
const InviteUserIcon = () => (
  <svg viewBox="0 0 20 20" className={ICON} aria-hidden data-icon="invite-user">
    <path fill="currentColor" d="m6.5,8c2.21,0,4-1.79,4-4S8.71,0,6.5,0,2.5,1.79,2.5,4s1.79,4,4,4Zm0-6c1.1,0,2,.9,2,2s-.9,2-2,2-2-.9-2-2,.9-2,2-2Z" />
    <path fill="currentColor" d="m12.24,11.55c-.84-1.61-2.32-2.55-4.24-2.55h-3c-3.04,0-5,2.36-5,6,0,.55.45,1,1,1s1-.45,1-1c0-1.2.29-4,3-4h3c1.3,0,2.04.64,2.46,1.44.18.34.51.56.89.56.74,0,1.23-.79.89-1.45Z" />
    <path fill="currentColor" d="m19,14.75h-2.25v-2.25c0-.55-.45-1-1-1s-1,.45-1,1v2.25h-2.25c-.55,0-1,.45-1,1s.45,1,1,1h2.25v2.25c0,.55.45,1,1,1s1-.45,1-1v-2.25h2.25c.55,0,1-.45,1-1s-.45-1-1-1Z" />
  </svg>
);

/** Legacy `components/icons/table-icons/WarningIcon.vue` with `color="red"` (Quasar red, #f44336). */
const WarningIcon = () => (
  <svg viewBox="0 0 24 24" className="size-[21px] shrink-0 text-[#f44336]" aria-hidden data-icon="warning">
    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.5" d="M12 16h.008M12 8v5m10-1c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10s10-4.477 10-10" />
  </svg>
);

const ITEM = 'flex h-12 cursor-pointer items-center gap-2 px-4 text-[14px] text-text-2 outline-none data-[highlighted]:bg-gray-50';

interface Action { readonly label: string; readonly icon: ReactNode; readonly onSelect: () => void; readonly negative?: boolean }

export function LocationRowMenu({ name, onEdit, onLogIn, onAddUser, onRemoveData }: {
  readonly name: string; readonly onEdit: () => void; readonly onLogIn: () => void; readonly onAddUser: () => void;
  /** Set only for a location with live items. */
  readonly onRemoveData?: (() => void) | undefined;
}) {
  const actions: readonly Action[] = [
    { label: 'Edit', icon: <EditIcon />, onSelect: onEdit },
    { label: 'Log into location', icon: <LoginIcon />, onSelect: onLogIn },
    { label: 'Add User', icon: <InviteUserIcon />, onSelect: onAddUser },
    ...(onRemoveData === undefined ? [] : [{ label: 'Remove Location Data', icon: <WarningIcon />, onSelect: onRemoveData, negative: true }]),
  ];
  return (
    <RowMenu label={`Actions for ${name}`} contentClassName="w-60 py-0">
      {actions.map((a, i) => (
        <Fragment key={a.label}>
          {i > 0 ? <DropdownMenu.Separator className="h-px bg-black/12" data-testid="location-menu-rule" /> : null}
          <DropdownMenu.Item className={a.negative === true ? `${ITEM} text-[#dc2626]!` : ITEM} onSelect={a.onSelect}>{a.icon}<span>{a.label}</span></DropdownMenu.Item>
        </Fragment>
      ))}
    </RowMenu>
  );
}
